/**
 * GA4 (gtag.js) + Meta Pixel optionnel — chargés uniquement après consentement cookies (RGPD).
 * Variables : VITE_GA_MEASUREMENT_ID, VITE_META_PIXEL_ID
 */

import type { Router } from 'vue-router'
import { initWebVitals } from './webVitals'

const GA_ID = (import.meta.env.VITE_GA_MEASUREMENT_ID || '').trim()
const META_PIXEL_ID = (import.meta.env.VITE_META_PIXEL_ID || '').trim()

type AnalyticsWindow = Window & {
  dataLayer?: unknown[]
  gtag?: (...args: unknown[]) => void
  fbq?: (...args: unknown[]) => void
  _fbq?: unknown
  _consentDefaultSet?: boolean
  _gaBooted?: boolean
}

let analyticsReady = false
let analyticsLoading = false

function win(): AnalyticsWindow {
  return window as AnalyticsWindow
}

function initDataLayer(): void {
  win().dataLayer = win().dataLayer || []
}

function defineGtag(): void {
  initDataLayer()
  const w = win()
  w.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    w.dataLayer!.push(arguments)
  } as NonNullable<AnalyticsWindow['gtag']>
}

export function hasConsent(): boolean {
  try {
    return localStorage.getItem('cookiesAccepted') === 'true'
  } catch {
    return false
  }
}

export function setConsentDefaultDenied(): void {
  if (typeof window === 'undefined' || win()._consentDefaultSet) return
  defineGtag()
  win().gtag!('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    wait_for_update: 500,
  })
  win()._consentDefaultSet = true
}

function grantConsent(): void {
  defineGtag()
  win().gtag!('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  })
}

function denyConsent(): void {
  defineGtag()
  win().gtag!('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
    if (existing?.dataset.loaded === 'true') {
      resolve()
      return
    }

    const script = existing ?? document.createElement('script')
    script.async = true
    script.src = src
    script.onload = () => {
      script.dataset.loaded = 'true'
      resolve()
    }
    script.onerror = () => reject(new Error(`Échec chargement script : ${src}`))
    if (!existing) document.head.appendChild(script)
  })
}

async function bootGa4(): Promise<void> {
  if (!GA_ID || win()._gaBooted) return

  defineGtag()
  grantConsent()

  await loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_ID}`)

  defineGtag()
  win().gtag!('js', new Date())
  win().gtag!('config', GA_ID, {
    anonymize_ip: true,
    send_page_view: true,
    page_path: window.location.pathname + window.location.search,
    page_location: window.location.href,
    page_title: document.title,
  })

  win()._gaBooted = true
}

function bootMetaPixel(): void {
  if (!META_PIXEL_ID || typeof win().fbq === 'function') return

  const w = win()
  const n: any = (w.fbq = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args)
  })
  if (!w._fbq) w._fbq = n
  n.push = n
  n.loaded = true
  n.version = '2.0'
  n.queue = []
  void loadScript('https://connect.facebook.net/en_US/fbevents.js').catch(() => {})
  w.fbq!('init', META_PIXEL_ID)
  w.fbq!('track', 'PageView')
}

export function pushPageView(path: string): void {
  if (!analyticsReady || !hasConsent() || !GA_ID || typeof win().gtag !== 'function') return

  win().gtag('config', GA_ID, {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  })
}

export function setupAnalyticsRouter(router: Router): void {
  router.afterEach((to) => {
    pushPageView(to.fullPath)
  })
}

export function loadGoogleAnalytics(): void {
  if (analyticsReady || analyticsLoading || !hasConsent() || !GA_ID) return

  analyticsLoading = true

  void (async () => {
    try {
      await bootGa4()
      bootMetaPixel()
      await initWebVitals()
      analyticsReady = true

      if (import.meta.env.DEV) {
        console.log(`✅ GA4 actif : ${GA_ID}`)
      }
    } catch (err) {
      console.error('Analytics : échec activation GA4', err)
    } finally {
      analyticsLoading = false
    }
  })()
}

export function disableGoogleAnalytics(): void {
  denyConsent()

  if (GA_ID && typeof win().gtag === 'function') {
    win().gtag('config', GA_ID, { send_page_view: false })
  }

  const fbq = win().fbq
  if (typeof fbq === 'function') {
    try {
      fbq('consent', 'revoke')
    } catch {
      /* ignore */
    }
  }

  analyticsReady = false
  analyticsLoading = false
  win()._gaBooted = false
}

export function initializeAnalytics(): void {
  setConsentDefaultDenied()
  if (hasConsent()) {
    loadGoogleAnalytics()
  } else if (import.meta.env.DEV) {
    initWebVitals()
    console.log('📊 Web Vitals (dev) — GA4 inactif sans consentement')
  }
}

if (typeof window !== 'undefined') {
  setConsentDefaultDenied()
}
